import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import {
  getAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
} from '../brain/api';

const DEFAULT_POLL_MS = 60_000;

/**
 * Load admin notifications with optional polling. When `enabled` is false, no requests run.
 */
export function useAdminNotifications({
  enabled = true,
  pollMs = DEFAULT_POLL_MS,
} = {}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(
    async ({ background = false } = {}) => {
      if (!enabled) return;
      if (!background) {
        setLoading(true);
      }
      try {
        const res = await getAdminNotifications({ type: 'admin', limit: 50 });
        const list = res?.success?.notifications ?? [];
        setNotifications(list);
        setUnreadCount(res?.success?.unreadCount ?? 0);
      } catch (e) {
        if (!background) {
          toast({
            title: 'Could not load alerts',
            description:
              e?.data?.failure?.message || e?.message || 'Request failed',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        if (!background) {
          setLoading(false);
        }
      }
    },
    [enabled, toast]
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    void load();
    const id = window.setInterval(() => {
      void load({ background: true });
    }, pollMs);
    return () => window.clearInterval(id);
  }, [enabled, load, pollMs]);

  const onMarkRead = useCallback(
    async (id) => {
      try {
        await markAdminNotificationRead(id);
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        toast({
          title: 'Mark read failed',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const onMarkAllRead = useCallback(async () => {
    try {
      await markAllAdminNotificationsRead({ type: 'admin' });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      toast({
        title: 'Mark all read failed',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [toast]);

  return {
    loading,
    notifications,
    unreadCount,
    load,
    onMarkRead,
    onMarkAllRead,
  };
}
